import { Component, computed, inject, model, signal, Signal } from '@angular/core';
import { form, FormField, maxLength, required, submit } from '@angular/forms/signals';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { SelectCompanyListComponent } from '@app/shared/components/select-company-list/select-company-list.component';
import { CreateCooperationRequest } from '@app/shared/services/dtos/cooperation/create-cooperation-request.dto';
import { Company } from '@app/shared/types/models/company/company.model';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { UiTextareaComponent } from '@app/shared/ui/textarea/ui-textarea.component';
import { TranslatePipe } from '@ngx-translate/core';

export interface CreateCooperationDialogData {
  companies: Signal<Company[]>;
}

const MAX_DESCRIPTION_LENGTH = 1000;

@Component({
  selector: 'proffeo-create-cooperation-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    TranslatePipe,
    UiDialogComponent,
    FormField,
    UiButtonComponent,
    SelectCompanyListComponent,
    UiTextareaComponent
  ],
  templateUrl: './create-cooperation-dialog.component.html'
})
export class CreateCooperationDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<CreateCooperationDialogComponent, CreateCooperationRequest>);

  protected readonly data = inject<CreateCooperationDialogData>(MAT_DIALOG_DATA);

  protected readonly maxDescriptionLength = MAX_DESCRIPTION_LENGTH;

  protected readonly model = signal({ description: '' });
  protected readonly form = form(this.model, path => {
    required(path.description, { message: 'errors.required' });
    maxLength(path.description, MAX_DESCRIPTION_LENGTH);
  });

  /** The maxlength message needs the limit as a translation parameter, so it is projected (shown while typing). */
  protected readonly descriptionTooLong = computed(() =>
    this.form
      .description()
      .errors()
      .some(error => error.kind === 'maxLength')
  );

  protected readonly canSave = computed(() => this.form().valid() && this.selectedCompanyId() !== undefined);

  protected selectedCompanyId = model<string | undefined>(undefined);

  protected save(): void {
    const recipientCompanyId = this.selectedCompanyId();
    if (!recipientCompanyId) return;
    void submit(this.form, {
      action: async () => {
        const value: CreateCooperationRequest = { description: this.model().description, recipientCompanyId };
        this.dialogRef.close(value);
      }
    });
  }
}
